import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { AccountCodesAPI } from '@webapp/api/AccountCodesAPI'
import { useProcurifyIntl } from '@webapp/hooks'
import { type AccountCode } from '@webapp/models'
import { parseAPIError } from '@webapp/utils/errors'
import messages from './messages'
import { toast } from '../Notification'
import { AccountCodeSelectStyled } from './styles/AccountCodeSelectStyled'

export interface IAccountCodeSelectProps<
  IsMulti extends boolean = false,
> extends IAsyncSelectProps<AccountCode, IsMulti> {}

export const AccountCodeSelect = <IsMulti extends boolean = false>({
  ...props
}: IAccountCodeSelectProps<IsMulti>) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps['loadOptions'] = (
    inputValue,
    pagination,
    callback
  ) => {
    const params = {
      search: inputValue,
      page: pagination.page,
      active: true,
    }

    AccountCodesAPI.getAccountCodes(params)
      .then((res) => {
        callback({
          options: res.data,
          pagination: {
            hasMore: !!res.metadata.pagination.next,
            page: pagination.page + 1,
          },
        })
      })
      .catch((err) => {
        toast.error({
          title: intl.formatMessage(messages.errorTitleText),
          children: parseAPIError(
            err.data,
            intl.formatMessage(messages.errorMessageText)
          ).message,
        })
        callback({
          options: null,
          pagination: { page: pagination.page },
        })
      })
  }

  return (
    <AccountCodeSelectStyled>
      <AsyncSelect
        cacheOptions={true}
        placeholder={intl.formatMessage(messages.accountCodeSelectPlaceholder)}
        loadOptions={loadOptions}
        getOptionLabel={(option) => `${option.code} - ${option.description}`}
        getOptionValue={(option) => (option ? option.id.toString() : null)}
        {...props}
      />
    </AccountCodeSelectStyled>
  )
}

AccountCodeSelect.displayName = 'AccountCodeSelect'
